import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, CircularProgress, Grid } from '@mui/material';
import { AxiosResponse } from 'axios';
import React, { useState, useEffect } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import {
  ClientDTO,
  ClientContactDTO,
  ClientStatusDTO,
  Field,
  FieldTypeEnum,
  findByName,
  getAllClientCustomFields,
  getClientFromDirectHire,
  getClientStatusListByVisibility,
  saveAndUpdateClient,
  csrfTokenCall,
  saveClientContacts,
  LogContextType,
  LogAction,
  saveActivityLog,
  getUserByEmail,
  ClientOwnerDTO,
  getIndustries,
  createIndustry,
  findClientByLinkedinUrl,
} from 'wisestep-api';
import {
  ControlledTextArea,
  ControlledTextInput,
  IUserDTO,
  Label,
  WsButton,
  WsLocationSearch,
  WsSelect,
  WsSelectObj,
} from 'wisestep-base';
import { object, string } from 'yup';

import { WsTagsFilter } from '../../../components/filters/components';
import RecruiterSuggestions from '../../../components/filters/components/RecruiterSuggestions';
import { useUserContext, useWorkspaceContext } from '../../../context';
import {
  getAvEditAddDesUiName,
  activityLogDTOHelper,
} from '../../../features/activityLog/ActivityHelper';
import { fetchAndSetAssignedUsers } from '../../../features/users/helper/hooks';
import { success, failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import {
  mapDirectHireClientToClientContactDTO,
  mapDirectHireClientToClientDTO,
  mergeFetchedClients,
} from '../helper/ClientFormHelper';
import { linkedInRegex } from '../helper/ClientListingHelper';
import { DirectHireClient } from '../types/DirectHireClient';

import CustomField from './CustomField';

const validationSchema = object().shape({
  name: string().required('Please provide Name'),
  linkedinUrl: string().required('Please provide LinkedIn URL').matches(linkedInRegex, {
    excludeEmptyString: true,
    message: 'Please provide a valid LinkedIn URL',
  }),
  website: string().nullable().notRequired().url('Please enter a valid website URL'),
  ownerId: string().required('Please provide Client Owner'),
});

interface ClientDetailsFormProps {
  source: string;
  setFormWizardCurrentStep?: (step: number) => void;
  client: ClientDTO;
  setClient?: (client: ClientDTO) => void;
  setClientContacts?: (clientContacts: ClientContactDTO[]) => void;
  setClientOwner?: (owner: ClientOwnerDTO) => void;
  clientContactsReceived?: ClientContactDTO[];
  clientFoundInDH?: boolean;
}

const ClientDetailsFormNew = ({
  source,
  setFormWizardCurrentStep,
  client,
  setClient,
  setClientContacts,
  setClientOwner,
  clientContactsReceived,
  clientFoundInDH,
}: ClientDetailsFormProps) => {
  const workspace = useWorkspaceContext();
  const params = useParams();
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      ...client,
      clientStatusId: client.clientStatus?.id || '',
    },
  });
  const {
    handleSubmit,
    getValues,
    reset,
    setValue,
    formState: { dirtyFields },
    watch,
  } = methods;
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [clientFoundInDirectHire, setClientFoundInDirectHire] = useState<boolean>(
    clientFoundInDH ?? false
  );
  const [token, setToken] = useState<string>('');
  const [auth, setAuth] = useState<string>('');
  const [ourClientStatusOptions, setOurClientStatusOptions] = useState<WsSelectObj[]>([]);
  const [customFields, setCustomFields] = useState<Field[]>();
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const [isLoading, setIsLoading] = useState(false);
  const [defaultClientStatus, setDefaultClientStatus] = useState<ClientStatusDTO>();
  const [fetchedContacts, setFetchedContacts] = useState<ClientContactDTO[]>(
    clientContactsReceived ?? []
  );
  const [showHiddenFields, setShowHiddenFields] = useState<boolean>(
    client.id || source === 'ClientForm' ? true : false
  );
  const [allStatuses, setAllStatuses] = useState<ClientStatusDTO[]>([]);
  const [industryOptions, setIndustryOptions] = useState<WsSelectObj[]>([]);
  const [existingClient, setExistingClient] = useState<ClientDTO | null>(null);
  const allowedWorkspaces = [
    'a61ed280-61fa-4e19-8e54-88bffcc24a75',
    'c809aa49-c696-4ea1-8467-0108a16ab786',
  ];

  const selectedEmail = watch('ownerId') ?? '';
  const name = watch('name');
  const linkedinUrl = watch('linkedinUrl');

  useEffect(() => {
    if (name || linkedinUrl) {
      setExistingClient(null);
    }
  }, [name, linkedinUrl]);

  useEffect(() => {
    const fetchOwner = async () => {
      if (selectedEmail) {
        try {
          const response = await getUserByEmail(selectedEmail);
          const { firstName = '', lastName = '' } = response.data || {};
          const recruiterName = `${firstName} ${lastName}`.trim();
          const owner = response.data ? { name: recruiterName, email: selectedEmail } : null;
          setValue('ownerId', selectedEmail);
          setValue('owner', owner);
        } catch {
          setValue('owner', null);
        }
      } else {
        setValue('owner', null);
      }
    };
    fetchOwner();
  }, [selectedEmail, setValue]);

  const getIndustryData = async () => {
    const response = await getIndustries();
    if (response?.data) {
      setIndustryOptions(response?.data);
    }
  };

  useEffect(() => {
    getIndustryData();
  }, []);

  useEffect(() => {
    const getClientStatusListDetails = async () => {
      const response = await getClientStatusListByVisibility();
      const optionsArray: WsSelectObj[] = [];
      let defaultStatusName = '';
      if (response?.data) {
        setAllStatuses(response.data);
      }
      response.data.forEach((obj: ClientStatusDTO) => {
        if (obj.makeDefaultStatus) {
          defaultStatusName = obj.id;
          methods.setValue('clientStatusId', obj.id);
          setDefaultClientStatus(obj);
        }
        optionsArray.push({
          label: obj.clientStatusName as string,
          value: obj.id ?? '',
        });
      });
      if (client?.clientStatus?.clientStatusName) {
        const selectedOption = optionsArray.find(
          (item) => item.label === client?.clientStatus?.clientStatusName
        );
        if (selectedOption) {
          methods.setValue('clientStatusId', selectedOption.value as string);
        }
      } else {
        methods.setValue('clientStatusId', defaultStatusName);
      }
      setOurClientStatusOptions(optionsArray);
    };

    getClientStatusListDetails();
  }, []);

  const saveActivityLogDetails = async (data: ClientDTO, response: AxiosResponse) => {
    if (!data) return;

    const changedFields = Object.keys(dirtyFields);
    if (changedFields.length === 0) return;

    const avPayload = {
      ...activityLogDTOHelper,
      parentEntitiyType: LogContextType.Client,
      entityId: response.data.data.id,
      parentEntitiyId: response.data.data.id,
      field: 'Client',
      entityName: response.data.data.name,
      contextData: { clientId: response.data.data.id },
      action: data.id ? LogAction.Edit : LogAction.Add,
    };

    if (data.id) {
      const emptyFields = Object.entries(client)
        .filter(([, v]) => v === '' || v === null)
        .map(([k]) => k);

      avPayload.description =
        getAvEditAddDesUiName('Client', emptyFields, changedFields) + ' under';
    } else {
      avPayload.description = 'ADDED';
    }

    await saveActivityLog(avPayload);
  };

  const onSubmit = async (data: ClientDTO) => {
    setIsLoading(true);
    setClientOwner && data.owner && setClientOwner(data.owner);
    try {
      if (data.clientStatusId) {
        const status = allStatuses.find((status) => status.id === data.clientStatusId);
        if (status) {
          data.clientStatus = status;
        }
      }
      setIsLoading(true);
      const allowedRecruiters = await fetchAndSetAssignedUsers(loggedInUser.email ?? '');
      data.allowedRecruiters = allowedRecruiters;
      const response = await saveAndUpdateClient(data);
      if (response && response.data) {
        const clientObj: ClientDTO = response.data.data;
        setClient && setClient({ ...clientObj, clientStatusId: clientObj.clientStatus?.id });
        if (fetchedContacts) {
          const updatedContacts = fetchedContacts.map((obj) => ({
            ...obj,
            clientId: clientObj.id,
            ownerId: clientObj.ownerId,
            owner: clientObj.owner,
          }));
          const contactResposne = await saveClientContacts(updatedContacts);
          if (contactResposne && contactResposne.data) {
            const savedContacts: ClientContactDTO[] = contactResposne.data.data;
            setClientContacts && setClientContacts(savedContacts);
          }
        }

        await saveActivityLogDetails(data, response);
        setNotification(success('Client details saved successfully'));
        setFormWizardCurrentStep && setFormWizardCurrentStep(1);
      }
    } catch (error) {
      console.error(error);
      setNotification(failed('Failed to save client details'));
    }
    setIsLoading(false);
  };

  const onProceed = async () => {
    const nameResponse = await findByName(getValues().name);
    if (nameResponse.data.data && nameResponse.data.data.id != getValues().id) {
      setExistingClient(nameResponse.data.data);
      return;
    }

    const linkedinResponse = await findClientByLinkedinUrl(getValues().linkedinUrl || '');
    if (linkedinResponse.data.data && linkedinResponse.data.data.id != getValues().id) {
      setExistingClient(linkedinResponse.data.data);
      return;
    }

    setExistingClient(null);

    if (!params.clientId && workspace?.id && allowedWorkspaces.includes(workspace?.id)) {
      const linkedinUrl = getValues().linkedinUrl?.replace(/\/$/, '') || '';
      const response = await getClientFromDirectHire({
        query: linkedinUrl,
        searchType: 'exact',
        searchBy: 'linkedinUrl',
      });
      if (response && response.data) {
        const directHireClients: DirectHireClient[] = response.data as DirectHireClient[];
        if (directHireClients.length > 0) {
          setClientFoundInDirectHire(true);
          const mergedClient = mergeFetchedClients(directHireClients);
          const clientDTO = mapDirectHireClientToClientDTO(mergedClient);
          const clientContacts = mapDirectHireClientToClientContactDTO(mergedClient);
          setFetchedContacts(clientContacts);
          if (clientDTO.industry) {
            const industry = industryOptions.find((option) => option.label === clientDTO.industry);
            if (!industry) {
              const industryResp = await createIndustry({
                label: clientDTO.industry,
                source: 'directHire',
              });
              if (industryResp && industryResp.data) {
                clientDTO.industry = industryResp.data.value;
                await getIndustryData();
              }
            } else {
              clientDTO.industry = industry.value as string;
            }
          }
          clientDTO.clientStatusId = defaultClientStatus?.id;
          clientDTO.clientStatus = defaultClientStatus;
          clientDTO.owner = getClientOwner();
          clientDTO.ownerId = getClientOwner()?.email || '';
          reset(clientDTO);
        } else {
          reset({
            ...client,
            name: getValues().name,
            linkedinUrl: linkedinUrl,
            clientStatusId: defaultClientStatus?.id,
            clientStatus: defaultClientStatus,
            owner: getClientOwner(),
            ownerId: getClientOwner()?.email || '',
          });
          setFetchedContacts([]);
          setClientFoundInDirectHire(false);
        }
        setShowHiddenFields(true);
      }
    } else {
      setValue('ownerId', getClientOwner()?.email || '');
      setValue('owner', getClientOwner());
      setShowHiddenFields(true);
    }
  };

  const getClientOwner = () => {
    if (getValues().owner) {
      return getValues().owner;
    }
    return {
      name: loggedInUser.firstName + ' ' + loggedInUser.lastName,
      email: loggedInUser.email || '',
    };
  };

  useEffect(() => {
    (async () => {
      const { data } = await getAllClientCustomFields();
      setCustomFields(data);
    })();
  }, []);

  useEffect(() => {
    const getToken = async () => {
      const res = await csrfTokenCall();
      const token = res.config.headers?.['X-XSRF-TOKEN'];
      const auth = res.config.headers?.['Authorization'];
      setToken(token ?? '');
      setAuth(auth ?? '');
      return token;
    };
    getToken();
  }, []);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container py={1} xs={12} lg={12}>
          {source === 'ClientAdditionForm' && (
            <>
              <Grid item p={1} sm={6}>
                <ControlledTextInput
                  name="name"
                  type="text"
                  fullWidth
                  labelText="Client Name"
                  variant="outlined"
                  showMandatorySymbol={true}
                />
              </Grid>
              <Grid item p={1} sm={6}>
                <ControlledTextInput
                  name="linkedinUrl"
                  type="text"
                  fullWidth
                  labelText="Linkedin Url"
                  required
                  showMandatorySymbol={true}
                />
              </Grid>
              {existingClient && (
                <Grid item p={1} sm={12}>
                  <Alert severity="error">
                    {'The client you are trying to add already exists.' +
                      `${
                        existingClient?.owner && existingClient?.owner?.email !== loggedInUser.email
                          ? ` Please contact ${existingClient?.owner?.name} (${existingClient?.owner?.email}) to get access.`
                          : ''
                      }`}
                  </Alert>
                </Grid>
              )}
              <Grid item p={1} sm={12} display="flex" justifyContent="flex-end">
                <WsButton variant="contained" onClick={onProceed}>
                  Proceed
                </WsButton>
              </Grid>
            </>
          )}
          {showHiddenFields && (
            <>
              {clientFoundInDirectHire && (
                <Grid item p={1} sm={12}>
                  <Alert severity="success">Client Data successfully imported.</Alert>
                </Grid>
              )}
              <Grid item sm={6} p={1}>
                <WsLocationSearch
                  required
                  multiSelect={false}
                  name="location"
                  placeHolder="Location"
                  showMandatorySymbol={true}
                  wisestepApi={true}
                  token={token}
                  authorization={auth}
                />
              </Grid>
              <Grid item sm={6} p={1}>
                <WsSelect
                  label="Client Status Name"
                  name="clientStatusId"
                  selectArray={ourClientStatusOptions}
                  defaultValue={defaultClientStatus?.clientStatusName}
                  size="small"
                />
              </Grid>
              <Grid item p={1} sm={6}>
                <Label>Client Owner*</Label>

                <Controller
                  name="ownerId"
                  control={methods.control}
                  defaultValue={client?.owner?.email || loggedInUser.email}
                  render={({ field }) => (
                    <RecruiterSuggestions
                      {...field}
                      value={field.value || ''}
                      defaultValue={field.value ? [field.value] : []}
                      onSelectMultipleValues={(emails: string[]) => {
                        const selectedEmail = emails[0] || '';
                        field.onChange(selectedEmail);
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item p={1} sm={6}>
                <WsSelect
                  label="Industry Type"
                  name="industry"
                  defaultValue={client?.industry ?? ''}
                  selectArray={industryOptions}
                  size="small"
                />
              </Grid>
              <Grid item p={1} sm={6}>
                <ControlledTextInput
                  name="source"
                  type="text"
                  fullWidth
                  labelText="Source"
                  variant="outlined"
                />
              </Grid>
              <Grid item p={1} sm={6}>
                <WsTagsFilter placeHolder="Enter Tags" name="tags" label="Tags" />
              </Grid>
              {customFields &&
                customFields.map(
                  (field) =>
                    field.visible && (
                      <Grid
                        item
                        key={field.id}
                        p={1}
                        sm={field.type === FieldTypeEnum.Checkbox ? 11.5 : 5.75}
                      >
                        <CustomField {...field} name={`customFields.${field.name}`} />
                      </Grid>
                    )
                )}
              <Grid item p={1} sm={12}>
                <ControlledTextArea name="description" labelText="Description" minRows={5} />
              </Grid>
            </>
          )}
        </Grid>
        {showHiddenFields && (
          <Grid container justifyContent="flex-end" spacing={2}>
            <Grid item>
              <WsButton
                variant="contained"
                size="medium"
                type="submit"
                disabled={isLoading}
                endIcon={isLoading ? <CircularProgress size={17} color="secondary" /> : <></>}
              >
                {isLoading
                  ? 'Saving...'
                  : source === 'ClientAdditionForm'
                  ? client.id
                    ? 'Update'
                    : 'Create'
                  : 'Continue'}
              </WsButton>
            </Grid>
          </Grid>
        )}
      </form>
    </FormProvider>
  );
};

export default ClientDetailsFormNew;
